import dayjs from "dayjs"
import { ROUTES_LIST } from "router/routes"

/**
 * Blackout vocabulary shared by the Settings page and the Calendar surfaces.
 *
 * Anything page-specific (modal copy, form payload building, target-picker helpers) stays in
 * `pagesV2/pro/Settings/Blackout`; only what both features need lives here.
 */

export const BLACKOUT_SCOPES = {
  BUSINESS: "business",
  LISTING: "listing",
  CREW: "crew",
  EQUIPMENT: "equipment"
}

/**
 * Scope -> the pill rendered on a settings row, a day card or a week header.
 *
 * Two labels per scope: the bare noun names the scope in a menu or a pastel pill, while the
 * dark pill on a row or day card spells the whole thing out ("Equipment Blackout"), which is
 * what the designs show wherever the pill stands alone next to a date.
 */
export const SCOPE_TAG = {
  [BLACKOUT_SCOPES.BUSINESS]: {
    labelKey: "settings.blackout.tag_business",
    labelDefault: "Business",
    solidLabelKey: "settings.blackout.tag_business_solid",
    solidLabelDefault: "All Experiences Blackout",
    variant: "business"
  },
  [BLACKOUT_SCOPES.LISTING]: {
    labelKey: "settings.blackout.tag_listings",
    labelDefault: "Listings",
    solidLabelKey: "settings.blackout.tag_listings_solid",
    solidLabelDefault: "Listings Blackout",
    variant: "listings"
  },
  [BLACKOUT_SCOPES.CREW]: {
    labelKey: "settings.blackout.tag_crew",
    labelDefault: "Crew",
    solidLabelKey: "settings.blackout.tag_crew_solid",
    solidLabelDefault: "Crew Blackout",
    variant: "resource"
  },
  [BLACKOUT_SCOPES.EQUIPMENT]: {
    labelKey: "settings.blackout.tag_equipment",
    labelDefault: "Equipment",
    solidLabelKey: "settings.blackout.tag_equipment_solid",
    solidLabelDefault: "Equipment Blackout",
    variant: "resource"
  }
}

/**
 * Ids arrive from several endpoints (`/blackouts`, `/blackouts/calendar`, `/blackouts/targets`)
 * and are not guaranteed to be serialized as the same JSON type by all of them — a record can
 * carry `target_ids: ["12"]` while the target list returns `id: 12`. Comparing those with `===`
 * silently loses every selection, so all cross-endpoint id matching goes through these.
 */
export const isSameId = (first, second) => first != null && second != null && String(first) === String(second)

export const includesId = (ids = [], id) => ids.some((candidate) => isSameId(candidate, id))

export const DATE_DISPLAY_FORMAT = "MMM D"
export const DATE_DISPLAY_FORMAT_WITH_YEAR = "MMM D, YYYY"
export const DATE_VALUE_FORMAT = "YYYY-MM-DD"
export const TIME_DISPLAY_FORMAT = "hh:mm A"
export const TIME_VALUE_FORMAT = "HH:mm"

/** An entry with no times set blocks the whole day. */
export const isAllDayEntry = (entry) => !entry?.start_time && !entry?.end_time

export const isRangeEntry = (entry) => Boolean(entry?.end_date) && entry.end_date !== entry.start_date

/**
 * Whether a day's blackout closes the business, which is what earns a day the full-closure
 * treatment: dark cell, red chip, no count.
 *
 * Scope alone decides it, timed or not. `docs/blackouts-api.md` D8 originally reserved that
 * treatment for an *all-day* business closure and called a timed one partial, but the design
 * shows every Business Blackout in red — a closure that shuts the whole catalogue for part of a
 * day is still a different statement from three listings being unavailable, which is what the
 * grey partial state means.
 */
export const closesBusiness = (blackout) => blackout?.scope === BLACKOUT_SCOPES.BUSINESS

/** "08:00" -> "08:00 AM - 04:00 PM". Returns "" for all-day entries. */
export const formatTimeRange = (entry) => {
  if (isAllDayEntry(entry)) return ""
  const start = dayjs(entry.start_time, TIME_VALUE_FORMAT).format(TIME_DISPLAY_FORMAT)
  const end = dayjs(entry.end_time, TIME_VALUE_FORMAT).format(TIME_DISPLAY_FORMAT)
  return `${start} - ${end}`
}

/**
 * One entry as a compact label. The year is only rendered when `withYear` is set, so a
 * multi-entry summary can carry it once at the end instead of on every part.
 *
 * Same month:  "Jun 13 - 15"
 * Cross month: "Jun 28 - Jul 2"
 * Single day:  "Jun 10"
 */
export const formatEntry = (entry, withYear = false) => {
  const format = withYear ? DATE_DISPLAY_FORMAT_WITH_YEAR : DATE_DISPLAY_FORMAT
  const start = dayjs(entry.start_date)

  if (!isRangeEntry(entry)) return start.format(format)

  const end = dayjs(entry.end_date)
  if (start.isSame(end, "month")) return `${start.format(DATE_DISPLAY_FORMAT)} - ${end.format(withYear ? "D, YYYY" : "D")}`
  return `${start.format(DATE_DISPLAY_FORMAT)} - ${end.format(format)}`
}

/**
 * All entries of a blackout as one line, e.g. "Jun 10 · Jun 13 - 15 · Jul 2, 2026".
 * Only the final part carries the year, matching the design.
 */
export const formatEntries = (entries = []) => {
  if (!entries.length) return ""
  const sorted = [...entries].sort((first, second) => dayjs(first.start_date).valueOf() - dayjs(second.start_date).valueOf())
  return sorted.map((entry, index) => formatEntry(entry, index === sorted.length - 1)).join(" · ")
}

/* -------------------------------------------------------------------------- */
/* Deep links out of a blackout surface                                       */
/* -------------------------------------------------------------------------- */

/**
 * What the reservations list opens with when a blackout sends the guide there. `sort`/`search` are
 * the list's own defaults — the page rewrites the whole query string when `range`, `date` or `sort`
 * is missing, so a partial link would be thrown away before it ever filtered anything.
 */
const RESERVATIONS_LINK_DEFAULTS = { sort: "asc", search: "" }

/**
 * The lifecycle tab to open on.
 *
 * A blackout's conflicting bookings are normally the ones still ahead of the guide, so the link
 * opens "Upcoming". But the feed also reports conflicts on days that have already passed, whose
 * bookings are `completed` — Upcoming would show an empty list for a day the panel says has two
 * bookings affected. A window entirely in the past therefore opens "Completed" instead.
 *
 * Today counts as not-past: a booking later today is still upcoming. `YYYY-MM-DD` strings compare
 * correctly, so no date parsing is needed.
 */
const getReservationsStatus = (dateFrom, dateTo) => {
  const windowEnd = dateTo || dateFrom
  return windowEnd && windowEnd < dayjs().format(DATE_VALUE_FORMAT) ? "completed" : "upcoming"
}

/**
 * The reservations list, narrowed to a date window and the listings a blackout affects.
 *
 * A single day uses the list's own "Day" range so the switcher reads as a day; a span uses the
 * custom range, which is the only mode that can express arbitrary bounds. Custom mode still
 * carries `date` — the page's "required params" guard checks for it and would otherwise reset the
 * link to the default month view.
 */
export const buildReservationsLink = ({ dateFrom, dateTo, listingIds = [] }) => {
  if (!dateFrom) return null
  const params = new URLSearchParams(RESERVATIONS_LINK_DEFAULTS)
  const isSingleDay = !dateTo || dateTo === dateFrom

  params.set("status", getReservationsStatus(dateFrom, dateTo))
  params.set("range", isSingleDay ? "day" : "custom")
  params.set("date", dateFrom)
  if (!isSingleDay) {
    params.set("date_from", dateFrom)
    params.set("date_to", dateTo)
  }
  listingIds.forEach((id) => params.append("experiences[]", String(id)))

  return `${ROUTES_LIST.guideReservationsPath}?${params.toString()}`
}

/**
 * The listings a record affects, as ids — or none, meaning "do not narrow by listing".
 *
 * Business scope always returns none: it closes the whole catalogue, which is not a filter. The
 * other scopes return none as well whenever `impact.listings` is short of `listings_count`: that
 * array is paginated, and filtering by the ten ids it happened to send would quietly hide the
 * bookings on the other seven listings. A wider list is recoverable; a silently truncated one is
 * not.
 */
export const getBlackoutListingIds = (blackout) => {
  if (blackout?.scope === BLACKOUT_SCOPES.BUSINESS) return []
  const listings = blackout?.impact?.listings || []
  const total = blackout?.impact?.listings_count
  if (!listings.length || (total != null && listings.length < total)) return []
  return listings.map((listing) => String(listing.id))
}

/**
 * The outer bounds of a record's entries — a blackout can hold several disjoint ranges, and the
 * reservations list takes one window. `YYYY-MM-DD` strings compare correctly as strings.
 */
export const getBlackoutDateBounds = (blackout) => {
  const dated = (blackout?.entries || []).filter((entry) => entry.start_date)
  if (!dated.length) return null
  return {
    dateFrom: dated.reduce((earliest, entry) => (entry.start_date < earliest ? entry.start_date : earliest), dated[0].start_date),
    dateTo: dated.reduce((latest, entry) => {
      const end = entry.end_date || entry.start_date
      return end > latest ? end : latest
    }, dated[0].end_date || dated[0].start_date)
  }
}

/** "Review" on a blackout's conflict alert — the affected listings over the blackout's own dates. */
export const buildBlackoutReservationsLink = (blackout) =>
  buildReservationsLink({ ...getBlackoutDateBounds(blackout), listingIds: getBlackoutListingIds(blackout) })

/**
 * The listings a *day-resolved* blackout affects.
 *
 * `listings` is now sent for every scope, with crew and equipment resolved through the
 * resource→listing cascade. Nothing is inferred from `id` any more: it is the blackout record's
 * id, not a listing's — filtering the reservations list by it would return an unrelated
 * experience, or nothing.
 */
const getDayBlackoutListingIds = (blackout) => (blackout?.listings || []).map((listing) => String(listing.id))

/**
 * "Review" on a day card — the closure the guide clicked, over its own dates.
 *
 * The card's alert counts the bookings the *record* strands, not the ones landing on the single
 * day the panel happens to be open on, so the link has to span the record too: a card reading
 * "Sep 9 - 11 · 1 booking requires action" that opens a list filtered to Sep 9 shows no rows
 * whenever that booking falls on the 10th or 11th. `date` is the fallback for a feed that sends
 * no span, where the day is all that is known.
 */
export const buildDayBlackoutReservationsLink = (blackout, date) =>
  buildReservationsLink({
    dateFrom: blackout?.start_date || date,
    dateTo: blackout?.end_date || date,
    listingIds: getDayBlackoutListingIds(blackout)
  })

/**
 * Opens a reservations link in a new tab.
 *
 * Every "Review" affordance carries an external-link arrow, so it has to honour it. It is also the
 * behaviour the surfaces need: each one is a panel or a form over the page it would navigate away
 * from, and a route change unmounts them — the guide would lose the day they were reading, or an
 * in-progress form, to look something up.
 *
 * The app is on a hash router, so the target is `origin` + `#` + the in-app path.
 */
export const openReservationsLink = (link) => {
  window.open(`${window.location.origin}/#${link || ROUTES_LIST.guideReservationsPath}`, "_blank", "noopener,noreferrer")
}
